import React, { useMemo, useState } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import {
  buildFdeDirectory,
  buildFdeTodos,
  PILOT_PLAYBOOK_GUIDE,
  pilotsForFde,
  summarizeFdeLoad,
} from "../fdeSelectors";
import { FDE_RESOURCES } from "../resources";
import { formatDate, formatNumber } from "../selectors";
import { ClientOverview } from "./ClientOverview";
import { PilotsTable } from "./PilotsTable";
import { ResourceLinks } from "./ResourceLinks";
import { TodoList } from "./TodoList";

/** Assigned clients; opening one shows its full usage and HubSpot links. */
function ClientList({ fde, roster }) {
  const [openId, setOpenId] = useState(null);

  if (roster.loading) {
    return <div className="pl-empty">Loading assigned clients…</div>;
  }
  if (roster.error) {
    return <div className="pl-empty">Assigned clients could not be loaded.</div>;
  }
  if (fde.clients === null) {
    return (
      <div className="pl-empty">
        {fde.name} is not in the Addy FDE roster, so no clients are assigned yet.
      </div>
    );
  }
  if (!fde.clients.length) {
    return <div className="pl-empty">No client organizations are assigned yet.</div>;
  }

  return (
    <ul className="pl-clients">
      {fde.clients.map((client) => {
        const isOpen = openId === client.organizationId;

        return (
          <li key={client.organizationId} className={isOpen ? "is-open" : ""}>
            <button
              type="button"
              className="pl-client-toggle"
              aria-expanded={isOpen}
              onClick={() => setOpenId(isOpen ? null : client.organizationId)}
            >
              {isOpen ? <FaChevronDown aria-hidden="true" /> : <FaChevronRight aria-hidden="true" />}
              <span className="pl-strong">{client.name}</span>
              <span className="pl-muted">
                {client.assignedAt ? `yours since ${formatDate(client.assignedAt)}` : ""}
              </span>
            </button>
            {isOpen ? (
              <ClientOverview organizationId={client.organizationId} includeHubSpot />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The page an FDE opens: pick your name, see the pilots and clients you own,
 * what needs doing, and how each client is using Addy.
 */
export function FdeView({ pilots, fdeOptions, roster, onRetryRoster, selectedFdeId, onSelectFde }) {
  const [expandedMine, setExpandedMine] = useState(null);
  const [expandedUnassigned, setExpandedUnassigned] = useState(null);
  const directory = useMemo(
    () => buildFdeDirectory(fdeOptions, roster.engineers || []),
    [fdeOptions, roster.engineers],
  );
  const fde = directory.find((entry) => entry.id === selectedFdeId) || null;
  const ownPilots = useMemo(() => pilotsForFde(pilots, fde), [pilots, fde]);
  const todos = useMemo(() => buildFdeTodos(ownPilots), [ownPilots]);
  const unassigned = pilots.filter((pilot) => !pilot.fde);
  const teamLoad = useMemo(() => summarizeFdeLoad(directory, pilots), [directory, pilots]);
  const toggleIn = (setExpanded) => (dealId) =>
    setExpanded((current) => (current === dealId ? null : dealId));
  const urgentCount = todos.filter((todo) => todo.priority === "high").length;

  return (
    <div className="pl-fde">
      <ResourceLinks title="FDE knowledge" links={FDE_RESOURCES} />

      <section className="pl-card pl-card--pad pl-fde-picker">
        <div className="pl-field">
          <label className="pl-field-label" htmlFor="pl-fde-self">
            I am
          </label>
          <select
            id="pl-fde-self"
            className="pl-select"
            value={fde ? fde.id : ""}
            onChange={(event) => onSelectFde(event.target.value || null)}
          >
            <option value="">Choose your name</option>
            {directory.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </div>
        {fde ? (
          <div className="pl-chip-row">
            <span className="pl-chip">{formatNumber(ownPilots.length)} running pilots</span>
            <span className="pl-chip">
              {formatNumber(todos.length)} to-dos
              {urgentCount ? ` · ${urgentCount} urgent` : ""}
            </span>
            <span className="pl-chip">
              {fde.clients === null ? "—" : formatNumber(fde.clients.length)} clients
            </span>
          </div>
        ) : (
          <p className="pl-note">
            Pick your name to see your pilots, your clients and what needs doing.
            Your choice is remembered on this browser.
          </p>
        )}
        {roster.error ? (
          <div className="pl-inline-error" role="alert">
            Could not load the FDE roster, so assigned clients are missing.
            <button type="button" className="pl-btn" onClick={onRetryRoster}>
              Try again
            </button>
          </div>
        ) : null}
      </section>

      {fde ? (
        <>
          <section className="pl-card">
            <header className="pl-card-head">
              <div>
                <h2>What needs doing</h2>
                <p>
                  From your pilots' HubSpot deals and how the client is using
                  Addy, most urgent first.
                </p>
              </div>
            </header>
            <TodoList todos={todos} />
          </section>

          <section className="pl-card">
            <header className="pl-card-head">
              <div>
                <h2>Your pilots</h2>
                <p>Running pilots with you as the Forward Deployed Engineer in HubSpot.</p>
              </div>
            </header>
            <PilotsTable
              pilots={ownPilots}
              loading={false}
              expandedId={expandedMine}
              onToggle={toggleIn(setExpandedMine)}
              emptyMessage={
                fde.hubspotValue
                  ? "No running pilots have you as their FDE."
                  : `${fde.name} is not in HubSpot's FDE dropdown yet, so no pilot can be tagged with them.`
              }
            />
          </section>

          <section className="pl-card">
            <header className="pl-card-head">
              <div>
                <h2>Your clients</h2>
                <p>
                  Client organizations assigned to you in Addy. Open one for its
                  people, loans, daily usage and Ask Addy questions.
                </p>
              </div>
            </header>
            <ClientList fde={fde} roster={roster} />
          </section>
        </>
      ) : null}

      {unassigned.length ? (
        <section className="pl-card">
          <header className="pl-card-head">
            <div>
              <h2>Pilots without an FDE</h2>
              <p>Pick one up by setting yourself as its Forward Deployed Engineer in HubSpot.</p>
            </div>
          </header>
          <PilotsTable
            pilots={unassigned}
            loading={false}
            expandedId={expandedUnassigned}
            onToggle={toggleIn(setExpandedUnassigned)}
          />
        </section>
      ) : null}

      <section className="pl-card">
        <header className="pl-card-head">
          <div>
            <h2>Team load</h2>
            <p>Running pilots, open to-dos and assigned clients per FDE.</p>
          </div>
        </header>
        <div className="pl-table-wrap">
          <table className="pl-table">
            <thead>
              <tr>
                <th className="pl-left">FDE</th>
                <th>Running pilots</th>
                <th>To-dos</th>
                <th>Urgent</th>
                <th>Clients</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {teamLoad.map((row) => (
                <tr key={row.id} className={row.id === selectedFdeId ? "is-open" : ""}>
                  <td className="pl-left pl-strong">{row.name}</td>
                  <td>{formatNumber(row.pilotCount)}</td>
                  <td>{formatNumber(row.todoCount)}</td>
                  <td>{row.urgentCount ? row.urgentCount : <span className="pl-muted">0</span>}</td>
                  <td>{row.clientCount === null ? "—" : formatNumber(row.clientCount)}</td>
                  <td>
                    <button
                      type="button"
                      className="pl-btn pl-btn--small"
                      onClick={() => onSelectFde(row.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="pl-card pl-card--pad">
        <h3 className="pl-section-title">Pilot playbook</h3>
        <ol className="pl-guide">
          {PILOT_PLAYBOOK_GUIDE.map((item) => (
            <li key={item.step}>
              <strong>{item.step}</strong> {item.detail}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
